import { createSelector } from 'reselect';
import dotProp from 'dot-prop-immutable';
import { memoizeWith, uniq } from 'ramda';
import { selectors as coreSelectors } from 'mage-core';
import { selectors as i18nSelectors } from 'mage-i18n';
import { selectors as configSelectors } from 'mage-config';
import { MERCHANT_FULL_ROLE, NAME } from '../constants';
import { isWidget } from './widget';

export const appState = state => state[NAME] || {};

export const hasAccess = createSelector(
  coreSelectors.getClientRoles,
  isWidget,
  (getClientRoles, isWidget) => tokenHelper => {
    const roles = tokenHelper ? getClientRoles(tokenHelper) : [];
    return (roles.length > 0 && roles.includes(MERCHANT_FULL_ROLE)) || isWidget;
  },
);

/**
 * @NOTE
 * Used to translate entire objects from the yml file. E.g., for integration guides
 * Plese use mage-i18n translator if you want to translate normal content
 */
export const translationParser = createSelector(
  i18nSelectors.translations,
  i18nSelectors.locale,
  (translations, locale) =>
    memoizeWith(
      (...args) => args.toString(),
      (tPrefix, merchantCountry) => {
        const prio = uniq([locale, locale.substr(0, 2), 'en']);
        let result;
        while (result === undefined && prio.length) {
          const p = prio.shift();
          if (merchantCountry) {
            result =
              dotProp.get(translations, `${p}.${tPrefix}.${merchantCountry}`) ||
              dotProp.get(translations, `${p}.${tPrefix}.default`) ||
              dotProp.get(translations, `${p}.${tPrefix}`);
          } else {
            result = dotProp.get(translations, `${p}.${tPrefix}`);
          }
        }
        return result;
      },
    ),
);

/**
 * @NOTE
 * Used to translate arrays from the yml file. E.g., for integration guides
 * Plese use mage-i18n translator if you want to translate normal content
 */
export const arrayTranslator = tPrefix =>
  createSelector(translationParser, translationParser =>
    translationParser(tPrefix).map(({ link = false, text }) => ({
      link,
      text,
    })),
  );

export const extractProductNames = products => {
  let names = '';
  products.forEach((p, index) => {
    if (products.length - 1 > index) {
      names = names.concat(p.name, ', ');
    } else if (products.length - 1 === index) {
      names = names.concat(p.name);
    }
  });
  return names;
};

export const countrySpecificTranslationParser = createSelector(
  translationParser,
  t => (tPrefix, country) => {
    const translationWithPrefixAndCountry = t(`${tPrefix}.${country}`);
    const translationWithPrefixDefault = t(`${tPrefix}.default`);
    const translationDefault = t(tPrefix);
    return translationWithPrefixAndCountry || translationWithPrefixDefault || translationDefault;
  },
);

export const getTermsUrl = createSelector(configSelectors.terms, terms => terms.url);
export const getPrivacyPolicyUrl = createSelector(configSelectors.privacy, privacy => privacy.url);

export const getLoginUrl = createSelector(configSelectors.apps, apps => {
  const productsApp = 'products-fe';
  const products = apps[productsApp] || {};
  return products.url;
});

export const stripQueryParameters = url =>
  url?.includes('?') ? url.substring(0, url.indexOf('?')) : url;
